import React, { useState, useEffect, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import axios from '../../../../axiosConfig';
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import styles from './EconomicDataUS.module.css'; 
import CustomDropdown from './CustomDropdown'; 

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend);

const economicOptions = [
  { name: 'Real GDP', code: 'REAL_GDP' },
  { name: 'Real GDP per Capita', code: 'REAL_GDP_PER_CAPITA' },
  { name: 'Treasury Yield', code: 'TREASURY_YIELD' },
  { name: 'Federal Funds (Interest) Rate', code: 'FEDERAL_FUNDS_RATE' },
  { name: "CPI", code: "CPI"},
  { name: "Retail Sales", code: "RETAIL_SALES"},
  { name: "Durable Goods Orders", code: "DURABLES"},
  { name: 'Unemployment Rate', code: 'UNEMPLOYMENT' },
  { name: 'Nonfarm Payroll', code: 'NONFARM_PAYROLL' }
];

const intervalOptionsMap = {
  REAL_GDP: [
    { label: 'Quarterly', value: 'quarterly' },
    { label: 'Annual', value: 'annual' }
  ],
  TREASURY_YIELD: [
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Monthly', value: 'monthly' }
  ],
  FEDERAL_FUNDS_RATE: [
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Monthly', value: 'monthly' }
  ]
};

const maturityOptions = [
  { label: '3 Month', value: '3month' },
  { label: '2 Year', value: '2year' },
  { label: '5 Year', value: '5year' },
  { label: '7 Year', value: '7year' },
  { label: '10 Year', value: '10year' },
  { label: '30 Year', value: '30year' }
];

const yAxisLabels = {
    REAL_GDP: '$ Billions',
    REAL_GDP_PER_CAPITA: 'USD per Capita',
    TREASURY_YIELD: 'Percentage',
    FEDERAL_FUNDS_RATE: 'Percentage',
    CPI: 'Points',
    RETAIL_SALES: '$ Millions',
    DURABLES: '$ Millions',
    UNEMPLOYMENT: 'Percentage',
    NONFARM_PAYROLL: 'Thousands of Persons'
};
  
function EconomicDataUS() {
  const [selectedEconomies, setSelectedEconomies] = useState([]);
  const [pendingSelection, setPendingSelection] = useState(null); 
  const [selections, setSelections] = useState({}); 
  const [financialData, setFinancialData] = useState({});
  const [loading, setLoading] = useState(false);
  const [individualChartData, setIndividualChartData] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  const maturityDropdownRef = useRef(null);
  const intervalDropdownRef = useRef(null);

  useEffect(() => {
    if (pendingSelection?.economy === 'TREASURY_YIELD' && !pendingSelection.maturity) {
      maturityDropdownRef.current?.focus();
    } else if (pendingSelection?.economy) {
      intervalDropdownRef.current?.focus();
    }
  }, [pendingSelection]);

  const handleEconomyChange = (selectedValue) => {
    if (['REAL_GDP', 'TREASURY_YIELD', 'FEDERAL_FUNDS_RATE'].includes(selectedValue)) {
      setPendingSelection({ economy: selectedValue, maturity: '', interval: '' });
    } else {
      const key = selectedValue;
      if (!selectedEconomies.includes(key)) {
        setSelectedEconomies(prev => [...prev, key]);
        setSelections(prev => ({
          ...prev,
          [key]: { economy: selectedValue }
        }));
      }
    }
  };

  const handleMaturitySelect = (maturity) => {
    setPendingSelection(prev => ({
      ...prev,
      maturity
    }));
  };

  const handleIntervalSelect = (interval) => {
    if (pendingSelection) {
      const updated = { ...pendingSelection, interval };

      if (updated.economy !== 'TREASURY_YIELD' || (updated.maturity && updated.interval)) {
        const key = `${updated.economy}_${updated.interval}${updated.maturity ? `_${updated.maturity}` : ''}`;

        if (!selectedEconomies.includes(key)) {
          setSelectedEconomies(prev => [...prev, key]);
          setSelections(prev => ({
            ...prev,
            [key]: {
              economy: updated.economy,
              interval: updated.interval,
              maturity: updated.maturity
            }
          }));
        }
        setPendingSelection(null);
      } else {
        setPendingSelection(updated);
      }
    }
  };

  const handleDeleteEconomy = (key) => {
    setSelectedEconomies(prev => prev.filter(item => item !== key));
    setSelections(prev => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  const handleCompareButtonClick = () => {
    setShowDropdown(true);
  };

  const handleCompareDataClick = async () => {
    setLoading(true);
    try {
      const requestsPayload = selectedEconomies.map(key => {
        const selection = selections[key];
        const payload = { function: selection.economy };
        if (selection.interval) payload.interval = selection.interval;
        if (selection.maturity) payload.maturity = selection.maturity;
        return payload;
      });
      

      const response = await axios.post('/api/economics_batch_us/', { requests: requestsPayload });

      const replaceNaN = (obj) => {
        if (Array.isArray(obj)) {
          return obj.map(replaceNaN);
        } else if (obj !== null && typeof obj === 'object') {
          const newObj = {};
          for (let key in obj) {
            newObj[key] = replaceNaN(obj[key]);
          }
          return newObj;
        } else if (typeof obj === 'number' && isNaN(obj)) {
          return null;
        } else {
          return obj;
        }
      };

      const safeResponseData = replaceNaN(response.data);

      const newFinancialData = {};
      for (const [funcName, funcData] of Object.entries(safeResponseData)) {
        selectedEconomies.forEach(key => {
          const selection = selections[key];
      
          // Build the expected backend key based on selection
          const expectedBackendKey = `${selection.economy}${selection.interval ? `_${selection.interval}` : ''}${selection.maturity ? `_${selection.maturity}` : ''}`;
      
          if (expectedBackendKey === funcName && funcData && funcData.data) {
            newFinancialData[key] = funcData.data;
          }
        });
      }
      

      setFinancialData(newFinancialData);
    } catch (error) {
      console.error(`Error fetching data: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const filterDataByDate = (data, startDate, endDate) => {
      return data.filter(entry => {
        const date = new Date(entry.date);
        return (!startDate || date >= new Date(startDate)) && (!endDate || date <= new Date(endDate));
      });
    };
  
    const chartDataArray = selectedEconomies.map((key) => {
      const selection = selections[key];
      const economyName = economicOptions.find(option => option.code === selection.economy)?.name;

      const maturityLabel = selection.maturity
        ? maturityOptions.find(opt => opt.value === selection.maturity)?.label
        : null;

      const intervalLabel = selection.interval
        ? intervalOptionsMap[selection.economy]?.find(opt => opt.value === selection.interval)?.label
        : null;

      const details = [maturityLabel, intervalLabel].filter(Boolean).join(', ');

      const fullEconomyName = details ? `${economyName} (${details})` : economyName;
      
      const dataPoints = financialData[key]
        ? filterDataByDate(financialData[key], startDate, endDate).sort((a, b) => new Date(a.date) - new Date(b.date))
        : [];
  
      return {
        key,
        name: fullEconomyName,
        economyCode: selection.economy, 
        data: {
          labels: dataPoints.map(entry => entry.date),
          datasets: [{
            label: fullEconomyName,
            data: dataPoints.map(entry => entry.value),
            borderColor: getRandomColor(),
            fill: false,
            pointRadius: 0
          }]
        }
      };
    }).filter(chart => chart.data.datasets[0].data.length > 0);
  
    setIndividualChartData(chartDataArray);
  
  }, [financialData, startDate, endDate, selectedEconomies, selections]);
  

  const getRandomColor = () => {
    const letters = '0123456789ABCDEF';
    return `#${Array.from({ length: 6 }).map(() => letters[Math.floor(Math.random() * 16)]).join('')}`;
  };

  return (
    <div>
    <h3 style={{ marginTop: 30 }}>Historical Data</h3>
    <div className={styles.container}>
      {!showDropdown ? (
        <button onClick={handleCompareButtonClick} className={styles.button_get_data}>Plot</button>
      ) : (
        <div>
          <div className={styles.chartControls}>
            <CustomDropdown 
              options={economicOptions.map(option => ({ label: option.name, value: option.code }))} 
              onSelect={handleEconomyChange} 
              className={styles.select} 
              label="Select data:"
            />
          </div>

          {pendingSelection?.economy && (
            <div className={styles.intervalDropdown}>
              {pendingSelection.economy === 'TREASURY_YIELD' && (
                <CustomDropdown 
                  options={maturityOptions}
                  onSelect={handleMaturitySelect}
                  className={styles.intervalSelect}
                  label="Select Maturity:"
                  dropdownRef={maturityDropdownRef}
                />
              )}
              <CustomDropdown 
                options={intervalOptionsMap[pendingSelection.economy]}
                onSelect={handleIntervalSelect}
                className={styles.intervalSelect}
                label="Select Interval:"
                dropdownRef={intervalDropdownRef}
              />
            </div>
          )}

          <div className={styles.selectedEconomies}>
            {selectedEconomies.map(key => {
              const selection = selections[key];
              const selectedEconomy = economicOptions.find(option => option.code === selection.economy);

              const maturityLabel = selection.maturity
                ? maturityOptions.find(opt => opt.value === selection.maturity)?.label
                : null;
              const intervalLabel = selection.interval
                ? intervalOptionsMap[selection.economy]?.find(opt => opt.value === selection.interval)?.label
                : null;

              const parts = [];
              if (maturityLabel) parts.push(maturityLabel);
              if (intervalLabel) parts.push(intervalLabel);
              const detailText = parts.length > 0 ? ` (${parts.join(', ')})` : '';

              return (
                <div key={key} className={styles.economyTag}>
                  <span style={{ marginRight: '8px' }}>
                    {selectedEconomy.name}{detailText}
                  </span>
                  <button onClick={() => handleDeleteEconomy(key)} className={styles.deleteButton}>x</button>
                </div>
              );
            })}
          </div>

          <button onClick={handleCompareDataClick} className={styles.button}>Plot</button>

          <div className={styles.dateInputs}>
            <div>
              <label>From:</label>
              <input type="date" onChange={event => setStartDate(event.target.value)} value={startDate} className={styles.dateInput} />
            </div>
            <div>
              <label>To:</label>
              <input type="date" onChange={event => setEndDate(event.target.value)} value={endDate} className={styles.dateInput} />
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className={`${styles.chartWrapper} ${individualChartData.length === 1 ? styles.singleChartWrapper : ''}`}>
          {individualChartData.map((chart) => (
            <div 
              key={chart.key} 
              className={individualChartData.length > 1 ? styles.chartContainerTwoPerRow : styles.chartContainer}
            >
              <div className={styles.scrollableChart}>
                <h3>{chart.name}</h3>
                <div className={styles.chartContent}>
                  <Line 
                    data={chart.data}
                    options={{
                        responsive: true,
                        plugins: {
                        legend: { display: false },
                        tooltip: {
                            bodyFont: {
                            size: window.innerWidth < 768 ? 12 : 14
                            },
                            titleFont: {
                            size: window.innerWidth < 768 ? 12 : 14
                            }
                        }
                        },
                        scales: {
                        x: {
                            ticks: { maxRotation: 0, autoSkipPadding: 20 }
                        },
                        y: {
                            beginAtZero: false,
                            title: {
                            display: true,
                            text: yAxisLabels[chart.economyCode] || ''

                            }
                        }
                        }
                    }}
                   />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    </div>
  );
}

export default EconomicDataUS;
